import MileageListParams, {
  MileageListParamsOrder,
} from "../../../../common/services/mileage/list/mileage-list-params";
import MileageListingFilter, {
  MileageListingCardNumberFilter,
  MileageListingMileageDateFilter,
  MileageListingDistanceFilter,
  MileageListingEndAddressFilter,
  MileageListingHumanIdFilter,
  MileageListingMileageNumberFilter,
  MileageListingStartAddressFilter,
  MileageListingWorkerNameFilter,
  MileageListingCargoCompanyNameFilter,
  MileageListingVehicleTypeFilter,
  MileageListingStateFilter,
} from "./types/mileage-listing-filter";
import MileageListingFilterType from "./types/mileage-listing-filter-type";
import MileageListingSortKey from "./types/mileage-listing-sort-key";
import MileageListingVehicleType from "./types/mileage-listing-vehicle-type";
import MileageListingStatus from "./types/mileage-listing-status";
import VehicleType from "../../../../common/types/vehicle-type";
import MileageStatus from "../../../../common/types/mileage-status";

const createVehicleType = (
  type: MileageListingVehicleType | undefined,
): VehicleType | undefined => {
  switch (type) {
    case MileageListingVehicleType.CAR_GT_900CM3:
      return VehicleType.CAR_GT_900CM3;
    case MileageListingVehicleType.CAR_LE_900CM3:
      return VehicleType.CAR_LE_900CM3;
    case MileageListingVehicleType.COMPANY_OWNED:
      return VehicleType.COMPANY_OWNED;
    case MileageListingVehicleType.MOPED:
      return VehicleType.MOPED;
    case MileageListingVehicleType.MOTORCYCLE:
      return VehicleType.MOTORCYCLE;
    default:
      return undefined;
  }
};

const createOrder = (
  sortKey: MileageListingSortKey | null,
): MileageListParamsOrder | undefined => {
  switch (sortKey) {
    case MileageListingSortKey.CARD_NO_ASC:
      return MileageListParamsOrder.CARD_NO_ASC;
    case MileageListingSortKey.CARD_NO_DESC:
      return MileageListParamsOrder.CARD_NO_DESC;
    case MileageListingSortKey.DATE_ASC:
      return MileageListParamsOrder.DATE_ASC;
    case MileageListingSortKey.DATE_DESC:
      return MileageListParamsOrder.DATE_DESC;
    case MileageListingSortKey.DISTANCE_ASC:
      return MileageListParamsOrder.DISTANCE_ASC;
    case MileageListingSortKey.DISTANCE_DESC:
      return MileageListParamsOrder.DISTANCE_DESC;
    case MileageListingSortKey.HUMAN_ID_ASC:
      return MileageListParamsOrder.HUMAN_ID_ASC;
    case MileageListingSortKey.HUMAN_ID_DESC:
      return MileageListParamsOrder.HUMAN_ID_DESC;
    case MileageListingSortKey.WORKER_NAME_ASC:
      return MileageListParamsOrder.WORKER_NAME_ASC;
    case MileageListingSortKey.WORKER_NAME_DESC:
      return MileageListParamsOrder.WORKER_NAME_DESC;
    case MileageListingSortKey.CARGO_COMPANY_NAME_ASC:
      return MileageListParamsOrder.CARGO_COMPANY_NAME_ASC;
    case MileageListingSortKey.CARGO_COMPANY_NAME_DESC:
      return MileageListParamsOrder.CARGO_COMPANY_NAME_DESC;
    case MileageListingSortKey.WORKER_LAST_NAME_ASC:
      return MileageListParamsOrder.WORKER_LAST_NAME_ASC;
    case MileageListingSortKey.WORKER_LAST_NAME_DESC:
      return MileageListParamsOrder.WORKER_LAST_NAME_DESC;
    default:
      return undefined;
  }
};

const createState = (
  type: MileageListingStatus | undefined,
): MileageStatus | undefined => {
  switch (type) {
    case MileageListingStatus.PENDING:
      return MileageStatus.PENDING;
    case MileageListingStatus.ACCEPTED:
      return MileageStatus.ACCEPTED;
    case MileageListingStatus.REJECTED:
      return MileageStatus.REJECTED;
    case MileageListingStatus.DELEGATION_CREATED:
      return MileageStatus.DELEGATION_CREATED;
    default:
      return undefined;
  }
};

const create = (
  page: number,
  pageSize: number,
  filters: MileageListingFilter[],
  sortKey: MileageListingSortKey | null,
): MileageListParams => {
  const dateFilter = filters.find(
    (filter) => filter.type == MileageListingFilterType.MILEAGE_DATE,
  )?.value as MileageListingMileageDateFilter["value"] | undefined;

  const distanceFilter = filters.find(
    (filter) => filter.type == MileageListingFilterType.DISTANCE,
  )?.value as MileageListingDistanceFilter["value"] | undefined;

  const startAddress = filters.find(
    (filter) => filter.type == MileageListingFilterType.START_ADDRESS,
  )?.value as MileageListingStartAddressFilter["value"] | undefined;

  const endAddress = filters.find(
    (filter) => filter.type == MileageListingFilterType.END_ADDRESS,
  )?.value as MileageListingEndAddressFilter["value"] | undefined;

  const vehicleType = filters.find(
    (filter) => filter.type == MileageListingFilterType.VEHICLE_TYPE,
  )?.value as MileageListingVehicleTypeFilter["value"] | undefined;

  const state = filters.find(
    (filter) => filter.type == MileageListingFilterType.STATE,
  )?.value as MileageListingStateFilter["value"] | undefined;

  return {
    page: page,
    pageSize: pageSize,
    order: createOrder(sortKey),
    dateSince: dateFilter?.from,
    dateTo: dateFilter?.to,
    humanId: filters.find(
      (filter) => filter.type == MileageListingFilterType.HUMAN_ID,
    )?.value as MileageListingHumanIdFilter["value"] | undefined,
    mileageNumber: filters.find(
      (filter) => filter.type == MileageListingFilterType.MILEAGE_NUMBER,
    )?.value as MileageListingMileageNumberFilter["value"] | undefined,
    workerName: filters.find(
      (filter) => filter.type == MileageListingFilterType.WORKER_NAME,
    )?.value as MileageListingWorkerNameFilter["value"] | undefined,
    cardNumber: filters.find(
      (filter) => filter.type == MileageListingFilterType.CARD_NUMBER,
    )?.value as MileageListingCardNumberFilter["value"] | undefined,
    cargoCompanyName: filters.find(
      (filter) => filter.type == MileageListingFilterType.CARGO_COMPANY_NAME,
    )?.value as MileageListingCargoCompanyNameFilter["value"] | undefined,
    vehicleType: createVehicleType(vehicleType),
    state: createState(state),
    startAddress: startAddress ? [startAddress] : undefined,
    endAddress: endAddress ? [endAddress] : undefined,
    distanceMin: distanceFilter?.from,
    distanceMax: distanceFilter?.to,
  };
};

const mileageListingLoadParamsFactory = {
  create,
};

export default mileageListingLoadParamsFactory;
